import { type TFunction } from 'react-i18next';

import {
  getDate,
  formatTimestamp,
} from 'components/Inputs/DateTimeInput/helpers';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import type { GenericOption } from 'pages/AutomationEngine/types';
import type { VariableDetails } from 'pages/AutomationEngine/dialogs/VariableWizard/types';

import {
  type VariableNumberFormatTypes,
  getBooleanString,
  getFormatString,
  getVariableFormatMetaData,
  getNumberString,
  getWebhookValueLabel,
} from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/format-meta-data';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const isStringArray = (arr: any[]): arr is string[] =>
  typeof arr?.[0] === 'string';

export type GetDescriptionValue = {
  details: VariableDetails;
  t: TFunction;
};

export const numberFormatted = (num: string) => {
  const formated = Number(num);
  return isNaN(formated) ? num : formated.toLocaleString();
};

export const wrapWithQuotes = (str?: string | null) => (str ? `‘${str}’` : '');

export const getDescriptionValue = ({ details, t }: GetDescriptionValue) => {
  let descriptionValue: string = t('Unknown');

  let descriptionPlural = '';
  let staticCount = 0;
  let staticOnly = false;

  const { variable } = details;
  const outputMetaData = getVariableFormatMetaData(variable?.dataType);

  if (details?.sources?.length) {
    const firstSource = details.sources[0];
    staticOnly =
      variable?.isArray &&
      firstSource?.sourceType === 'static' &&
      details.sources.length === 1;

    switch (firstSource?.sourceType) {
      case 'static': {
        const { staticVariable } = firstSource;

        if (variable?.isArray && Array.isArray(staticVariable?.value)) {
          staticCount = staticVariable?.value?.length;
        }

        if (!staticVariable?.value) {
          descriptionValue = '';
        } else if (Array.isArray(staticVariable?.value)) {
          descriptionValue = isStringArray(staticVariable.value)
            ? staticVariable.value[0]
            : staticVariable.value[0]?.label ?? '';
        } else {
          descriptionValue =
            typeof staticVariable.value === 'object'
              ? staticVariable.value.label ?? ''
              : (staticVariable.value ?? '').replace(/\n/g, '');
        }

        if (
          descriptionValue &&
          variable.dataType === AUTOMATION_VARIABLE_DATA_TYPES.datetime
        ) {
          try {
            const date = new Date(descriptionValue);
            descriptionValue = wrapWithQuotes(
              format(
                date,
                getFormatString(outputMetaData!, variable.outputFormat!)!
              )
            );
          } catch (error) {
            descriptionValue = wrapWithQuotes(
              formatTimestamp(descriptionValue)
            );
          }
        } else if (
          descriptionValue &&
          variable.dataType === AUTOMATION_VARIABLE_DATA_TYPES.date
        ) {
          try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            descriptionValue = wrapWithQuotes(
              formatInTimeZone(
                descriptionValue,
                timeZone,
                getFormatString(outputMetaData!, variable.outputFormat!)!
              )
            );
          } catch (error) {
            descriptionValue = wrapWithQuotes(getDate(descriptionValue));
          }
        } else if (
          descriptionValue &&
          variable.dataType === AUTOMATION_VARIABLE_DATA_TYPES.boolean
        ) {
          try {
            const booleanString = getBooleanString(
              outputMetaData!,
              variable.outputFormat!,
              descriptionValue === 'True'
            );

            descriptionValue = wrapWithQuotes(booleanString);
          } catch (error) {
            descriptionValue = wrapWithQuotes(
              descriptionValue === 'True' ? t('Yes') : t('No')
            );
          }
        } else if (
          descriptionValue &&
          variable.dataType === AUTOMATION_VARIABLE_DATA_TYPES.number
        ) {
          descriptionValue = wrapWithQuotes(
            getNumberString(
              descriptionValue,
              variable.outputFormat! as VariableNumberFormatTypes
            )
          );
        } else {
          descriptionValue =
            wrapWithQuotes(descriptionValue) || t('<i>empty</i>');
        }
        break;
      }
      case 'logged_activity_trigger': {
        const { loggedActivityVariable } = firstSource;

        switch (loggedActivityVariable?.sourceSubtype) {
          case 'logged_by':
            descriptionValue = t('Activity Logged By Employee');
            break;

          case 'logged_at':
            descriptionValue = t('Activity Logged Time');
            break;

          case 'due_datetime':
            descriptionValue = t('Logged Activity Due Date');
            break;

          case 'activity_field_value':
            descriptionValue = t('activity field {{label}}', {
              label: wrapWithQuotes(
                (loggedActivityVariable?.value as GenericOption)?.label
              ),
            });
            break;

          case 'record_association':
            descriptionValue = t('Activity Association {{label}}', {
              label: wrapWithQuotes(
                (loggedActivityVariable?.value as GenericOption)?.label
              ),
            });
            break;

          case 'logged_activity_id':
            descriptionValue = t('Logged Activity ID');
            break;

          case 'scheduled_activity_id':
            descriptionValue = t('Scheduled Activity ID');
            break;

          case 'activity_object_id':
            descriptionValue = t('Activity Object ID');
            break;

          default:
            break;
        }
        break;
      }
      case 'activity_past_due_trigger': {
        const { activityPassedDueVariable } = firstSource;

        switch (activityPassedDueVariable?.sourceSubtype) {
          case 'scheduled_at':
            descriptionValue = t('Scheduled At');
            break;

          case 'assignee':
            descriptionValue = t('Activity Assignee');
            break;

          case 'assignee_role':
            descriptionValue = t('Activity Assignee (Role)');
            break;

          case 'due_datetime':
            descriptionValue = t('Activity Due Date');
            break;

          case 'record_association':
            descriptionValue = t('Activity Association {{label}}', {
              label: wrapWithQuotes(
                (activityPassedDueVariable?.value as GenericOption)?.label
              ),
            });
            break;

          case 'scheduled_activity_id':
            descriptionValue = t('Scheduled Activity ID');
            break;

          case 'activity_object_id':
            descriptionValue = t('Activity Object ID');
            break;

          default:
            break;
        }
        break;
      }
      case 'field_value': {
        const { fieldVariable } = firstSource;
        descriptionValue = wrapWithQuotes(fieldVariable?.field?.label);
        break;
      }

      case 'variable': {
        const { automationVariable: { value } = {} } = firstSource;
        descriptionValue = wrapWithQuotes(
          typeof value === 'string'
            ? value
            : Array.isArray(value)
              ? value?.[0]?.label
              : value?.label
        );
        break;
      }

      case 'trigger_time': {
        descriptionValue = t('current time');
        break;
      }

      case 'webhook_trigger': {
        const { webhookVariable: { value, valueCategory } = {} } = firstSource;
        const deletedPrefix =
          value?.deleted || valueCategory?.deleted ? `${t('[Deleted]')} ` : '';
        if (
          ['meta_query_string', 'meta_body_content'].includes(value?.value!)
        ) {
          descriptionValue = `${deletedPrefix}${valueCategory?.value || ''} ${getWebhookValueLabel(value?.value || '', t) || ''}`;
          break;
        } else {
          descriptionValue = wrapWithQuotes(
            `${deletedPrefix}${valueCategory?.value || ''}.${value?.value || ''}`
          );
        }
        break;
      }

      default:
        break;
    }

    if (staticOnly) {
      descriptionValue =
        staticCount > 0
          ? t('[{{amount}} {{items}}]', {
              amount: staticCount,
              items: staticCount > 1 ? t('items') : t('item'),
            })
          : '[]';
    } else if (details.sources?.length > 1) {
      // add the plural
      descriptionPlural = t(', and {{N}} more', {
        N: details.sources.length! - 1,
      });
    }
  } else {
    if (variable.isArray) {
      descriptionValue = '[]';
    } else {
      descriptionValue = t('<i>empty</i>');
    }
  }

  return { descriptionValue, descriptionPlural };
};
